import React, { useState, useEffect, useRef } from 'react';
import {
  Dices,
  Star,
  Sparkle,
  Moon,
  Hourglass,
  Play,
  Pause,
  Square,
  RotateCcw,
  Music4,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";

// Big whimsical doodles drifting behind the content.
const DOODLES = [
  { Icon: Hourglass, top: "12%", left: "7%", size: 70, opacity: 0.14, delay: "0s" },
  { Icon: Star, top: "20%", left: "86%", size: 46, opacity: 0.16, delay: "1.4s" },
  { Icon: Moon, top: "74%", left: "88%", size: 68, opacity: 0.12, delay: "0.7s" },
  { Icon: Sparkle, top: "82%", left: "8%", size: 44, opacity: 0.15, delay: "2.2s" },
];

const Timer = () => {
  const [time, setTime] = useState(0);
  const [initialTime, setInitialTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedGame, setSelectedGame] = useState('');
  const [customTime, setCustomTime] = useState(0);
  const [customMusic, setCustomMusic] = useState(null);
  const [endingSound, setEndingSound] = useState(null);
  const [enableWarnings, setEnableWarnings] = useState(false);
  const [enabledAnnouncements, setEnabledAnnouncements] = useState([]);
  const [backgroundAudio, setBackgroundAudio] = useState(null);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [musicVolume, setMusicVolume] = useState(0.5);
  const [warningVolume, setWarningVolume] = useState(1.0);
  const [endingVolume, setEndingVolume] = useState(1.0);
  const [savedPresets, setSavedPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [customHours, setCustomHours] = useState(0);
  const [customMinutes, setCustomMinutes] = useState(0);
  const [customSeconds, setCustomSeconds] = useState(0);

  const backgroundAudioRef = useRef(null);

  const backgroundSounds = [
    { label: "Exciting Music 01", value: `sounds/exciting-music-01.mp3`, duration: 146 },
  ];

  const timeAnnouncementOptions = [
    {label: "5 seconds left", value: `sounds/5secs.mp3`, time: 5, duration: 1},
    {label: "10 seconds left", value: `sounds/10secs.mp3`, time: 10, duration: 1},
    {label: "30 seconds left", value: `sounds/30secs.mp3`, time: 30, duration: 1},
    {label: "1 minute left", value: `sounds/1min.mp3`, time: 60, duration: 1},
    {label: "1 minute 30 seconds left", value: `sounds/1m30s.mp3`, time: 90, duration: 1},
  ];

  const endingSounds = [
    { label: "Buzzer 01", value: `sounds/buzzer.mp3`, duration: 1 },
    { label: "Finished Bell", value: `sounds/finished_bell.wav`, duration: 6 },
  ];

  const gameSettings = {
    "Fit to Print": { time: 240, music: { value: `sounds/exciting-music-01.mp3`, duration: 146 } },
  };

  const toggleAnnouncement = (announcement) => {
    if (enabledAnnouncements.some(a => a.time === announcement.time)) {
      setEnabledAnnouncements(enabledAnnouncements.filter(a => a.time !== announcement.time));
    } else {
      setEnabledAnnouncements([...enabledAnnouncements, announcement].sort((a, b) => b.time - a.time));
    }
  };

  const playDynamicSound = (sound, timerTime, soundDuration, volume = 1.0) => {
    if (!sound || timerTime <= 0 || soundDuration <= 0) return null;
    const audio = new Audio(sound.value);
    audio.volume = volume;
    const startTime = Math.max(0, soundDuration - timerTime);
    audio.currentTime = startTime;
    audio.play().catch(err => console.error('Error playing audio:', err));
    return audio;
  };

  const playSound = (sound, volume = 1.0) => {
    if (!sound) return;
    const audio = new Audio(sound.value);
    audio.volume = volume;
    audio.play().catch(err => console.error('Error playing audio:', err));
  };

  const testSound = (sound, volume = 1.0) => {
    if (!sound) return;
    playSound(sound, volume);
  };

  const adjustTime = (seconds) => {
    setTime((prevTime) => Math.max(0, prevTime + seconds));
  };

  const applyCustomTime = () => {
    const totalSeconds = customHours * 3600 + customMinutes * 60 + customSeconds;
    if (totalSeconds > 0) {
      setCustomTime(totalSeconds);
      setTime(totalSeconds);
      setInitialTime(totalSeconds);
    }
  };

  const setQuickTime = (seconds) => {
    setCustomTime(seconds);
    setTime(seconds);
    setInitialTime(seconds);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    setCustomHours(hours);
    setCustomMinutes(minutes);
    setCustomSeconds(secs);
  };

  // Calculate current custom time from inputs for display
  const currentCustomTime = customHours * 3600 + customMinutes * 60 + customSeconds;

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const savePreset = () => {
    if (!presetName.trim()) {
      alert('Please enter a name for your preset');
      return;
    }

    const preset = {
      id: Date.now(),
      name: presetName.trim(),
      selectedGame,
      customTime,
      customMusic,
      endingSound,
      enableWarnings,
      enabledAnnouncements: [...enabledAnnouncements],
      musicVolume,
      warningVolume,
      endingVolume,
    };

    const updatedPresets = [...savedPresets, preset];
    setSavedPresets(updatedPresets);
    localStorage.setItem('timerPresets', JSON.stringify(updatedPresets));
    setPresetName('');
  };

  const loadPreset = (preset) => {
    setSelectedGame(preset.selectedGame);
    setCustomTime(preset.customTime);
    setCustomMusic(preset.customMusic);
    setEndingSound(preset.endingSound);
    setEnableWarnings(preset.enableWarnings);
    setEnabledAnnouncements(preset.enabledAnnouncements || []);
    setMusicVolume(preset.musicVolume ?? 0.5);
    setWarningVolume(preset.warningVolume ?? 1.0);
    setEndingVolume(preset.endingVolume ?? 1.0);
  };

  const deletePreset = (id) => {
    const updatedPresets = savedPresets.filter(p => p.id !== id);
    setSavedPresets(updatedPresets);
    localStorage.setItem('timerPresets', JSON.stringify(updatedPresets));
  };

  // Load presets from localStorage on mount
  useEffect(() => {
    const storedPresets = localStorage.getItem('timerPresets');
    if (storedPresets) {
      try {
        setSavedPresets(JSON.parse(storedPresets));
      } catch (err) {
        console.error('Error loading presets:', err);
      }
    }
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(err => console.error('Error requesting notification permission:', err));
    }
  }, []);

  const showNotification = (title, body) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/favicon.ico',
      });
    }
  };

  const startTimer = () => {
    if (!selectedGame && customTime <= 0) return;

    let timerDuration = customTime;
    let timerMusic = customMusic;

    if (selectedGame && selectedGame !== "Other") {
      const settings = gameSettings[selectedGame];
      timerDuration = settings.time;
      timerMusic = settings.music;
    }

    setTime(timerDuration);
    setInitialTime(timerDuration);
    if (timerMusic) {
      const newBackgroundAudio = playDynamicSound(timerMusic, timerDuration, timerMusic.duration, musicVolume);
      setBackgroundAudio(newBackgroundAudio);
      backgroundAudioRef.current = newBackgroundAudio;
    }

    setIsRunning(true);
  };

  const resumeTimer = () => {
    if (backgroundAudio) {
      backgroundAudio.currentTime = audioCurrentTime;
      backgroundAudio.volume = musicVolume;
      backgroundAudio.play().catch(err => console.error('Error resuming audio:', err));
    }
    setIsRunning(true);
  };

  const stopTimer = () => {
    setIsRunning(false);
    if (backgroundAudio) {
      backgroundAudio.pause();
      backgroundAudio.currentTime = 0;
    }
  };

  const pauseTimer = () => {
    setIsRunning(false);
    if (backgroundAudio) {
      setAudioCurrentTime(backgroundAudio.currentTime);
      backgroundAudio.pause();
    }
  };

  const restartTimer = () => {
    let timerDuration = customTime;
    let timerMusic = customMusic;

    if (selectedGame && selectedGame !== "Other") {
      const settings = gameSettings[selectedGame];
      timerDuration = settings.time;
      timerMusic = settings.music;
    }

    setTime(timerDuration);
    setInitialTime(timerDuration);
    if (timerMusic) {
      if (backgroundAudio) {
        backgroundAudio.pause();
        backgroundAudio.currentTime = 0;
      }
      const newBackgroundAudio = playDynamicSound(timerMusic, timerDuration, timerMusic.duration, musicVolume);
      setBackgroundAudio(newBackgroundAudio);
      backgroundAudioRef.current = newBackgroundAudio;
    }

    setIsRunning(true);
  };

  // Update audio volume when volume state changes
  useEffect(() => {
    if (backgroundAudio) {
      backgroundAudio.volume = musicVolume;
    }
  }, [musicVolume, backgroundAudio]);

  // Timer countdown effect
  useEffect(() => {
    let timer;
    if (isRunning && time > 0) {
      timer = setInterval(() => {
        setTime((prevTime) => {
          const currentTime = prevTime - 1;

          if (enableWarnings) {
            enabledAnnouncements.forEach((announcement) => {
              if (currentTime === announcement.time) {
                playSound(announcement, warningVolume);
              }
            });
          }

          return currentTime;
        });
      }, 1000);
    } else if (time === 0 && isRunning) {
      setIsRunning(false);
      if (endingSound) {
        playSound(endingSound, endingVolume);
      }
      showNotification('Timer Finished!', 'Your timer has reached zero.');
    }

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, time, enabledAnnouncements, enableWarnings, endingSound, warningVolume, endingVolume]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (backgroundAudioRef.current) {
        backgroundAudioRef.current.pause();
        backgroundAudioRef.current = null;
      }
      if (backgroundAudio) {
        backgroundAudio.pause();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ignore if user is typing in an input/select
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.isContentEditable) {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          if (isRunning) {
            pauseTimer();
          } else if (time > 0) {
            resumeTimer();
          } else {
            startTimer();
          }
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          if (initialTime > 0) {
            restartTimer();
          }
          break;
        case 's':
        case 'S':
          e.preventDefault();
          stopTimer();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, time, initialTime, selectedGame, customTime, customMusic]);

  const maxTime = selectedGame && selectedGame !== "Other"
    ? gameSettings[selectedGame].time
    : customTime;

  const progress = initialTime > 0 ? (time / initialTime) * 100 : 0;
  const isLowTime = time <= 30 && time > 0;
  const isCriticalTime = time <= 10 && time > 0;

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-dreamy">
      {/* Aurora glows. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-primary/15 blur-3xl animate-aurora"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-[hsl(var(--blue)/0.22)] blur-3xl animate-aurora"
        style={{ animationDelay: "-7s" }}
      />
      {DOODLES.map(({ Icon, top, left, size, opacity, delay }, i) => (
        <Icon
          key={i}
          aria-hidden
          className="pointer-events-none absolute text-primary animate-drift"
          style={{ top, left, width: size, height: size, opacity, animationDelay: delay }}
        />
      ))}

      <div className="container relative mx-auto max-w-3xl px-4 py-16">
        {/* Whimsical header. */}
        <p className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
          <Hourglass className="h-4 w-4" />
          keep game night moving
        </p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
          <span className="text-gold-shimmer">Board Game Timer</span>
        </h1>
        <p className="mt-5 max-w-xl text-lg text-muted-foreground">
          Pick a game or a time, add a little music, and let the countdown work
          its magic.{" "}
          <span className="text-foreground/80">
            Tap <kbd className="rounded bg-secondary px-1.5 py-0.5 text-xs">space</kbd> to
            pause, <kbd className="rounded bg-secondary px-1.5 py-0.5 text-xs">R</kbd> to
            restart, <kbd className="rounded bg-secondary px-1.5 py-0.5 text-xs">S</kbd> to stop.
          </span>
        </p>

        {/* The timer itself — the star of the show, up front. */}
        <Card className="mt-10 border-border bg-card/70 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-6">
              {/* Progress Bar */}
              {initialTime > 0 && (
                <div className="w-full max-w-md">
                  <div className="h-3 overflow-hidden rounded-full bg-secondary">
                    <div
                      className={`h-full transition-all duration-300 ${
                        isCriticalTime
                          ? 'bg-red-500 animate-pulse'
                          : isLowTime
                          ? 'bg-orange-400'
                          : 'bg-primary'
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="mt-1 text-center text-xs text-muted-foreground">
                    {Math.round(progress)}% remaining
                  </div>
                </div>
              )}

              {/* Timer Display */}
              <div
                className={`font-mono text-5xl font-bold transition-all duration-300 sm:text-6xl md:text-7xl ${
                  isCriticalTime
                    ? 'scale-110 text-red-500 animate-pulse'
                    : isLowTime
                    ? 'text-orange-400'
                    : 'text-gold-shimmer'
                }`}
              >
                {formatTime(time)}
              </div>

              {/* Time Adjustment Buttons */}
              {time > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => adjustTime(-300)} disabled={time < 300} title="Subtract 5 minutes">-5m</Button>
                    <Button variant="outline" size="sm" onClick={() => adjustTime(-60)} disabled={time < 60} title="Subtract 1 minute">-1m</Button>
                    <Button variant="outline" size="sm" onClick={() => adjustTime(-30)} disabled={time < 30} title="Subtract 30 seconds">-30s</Button>
                    <Button variant="outline" size="sm" onClick={() => adjustTime(-10)} disabled={time < 10} title="Subtract 10 seconds">-10s</Button>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => adjustTime(10)} title="Add 10 seconds">+10s</Button>
                    <Button variant="outline" size="sm" onClick={() => adjustTime(30)} title="Add 30 seconds">+30s</Button>
                    <Button variant="outline" size="sm" onClick={() => adjustTime(60)} title="Add 1 minute">+1m</Button>
                    <Button variant="outline" size="sm" onClick={() => adjustTime(300)} title="Add 5 minutes">+5m</Button>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  onClick={isRunning ? pauseTimer : (time > 0 ? resumeTimer : startTimer)}
                  size="lg"
                  className="min-w-[120px] gap-1.5"
                >
                  {isRunning ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  {isRunning ? "Pause" : time > 0 ? "Resume" : "Start"}
                </Button>
                <Button onClick={stopTimer} variant="outline" size="lg" className="min-w-[100px] gap-1.5">
                  <Square className="h-4 w-4" />
                  Stop
                </Button>
                <Button onClick={restartTimer} variant="outline" size="lg" disabled={initialTime === 0} className="min-w-[100px] gap-1.5">
                  <RotateCcw className="h-4 w-4" />
                  Restart
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Setup: game + music + custom time. */}
        <Card className="mt-6 border-border bg-card/70 backdrop-blur">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Dices className="h-5 w-5 text-primary" />
              The Setup
            </CardTitle>
            <CardDescription>Choose a game or set your own time, and pick some music.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="game-select">Select Game</Label>
              <Select value={selectedGame || undefined} onValueChange={(value) => {
                setSelectedGame(value);
                if (value !== "Other" && value in gameSettings) {
                  const settings = gameSettings[value];
                  setCustomTime(settings.time);
                  setCustomMusic(settings.music);
                }
              }}>
                <SelectTrigger id="game-select">
                  <SelectValue placeholder="Choose a Game" />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(gameSettings).map((game) => (
                    <SelectItem key={game} value={game}>{game}</SelectItem>
                  ))}
                  <SelectItem value="Other">Other (custom time)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Custom time — only when "Other". Two ways to set it, not four. */}
            {selectedGame === "Other" && (
              <div className="space-y-4 rounded-lg border border-border p-4">
                <Label>Custom Time</Label>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Quick presets</Label>
                  <div className="flex flex-wrap gap-2">
                    {[60, 90, 120, 180, 240, 300, 600].map((seconds) => (
                      <Button
                        key={seconds}
                        variant={customTime === seconds ? "default" : "outline"}
                        size="sm"
                        onClick={() => setQuickTime(seconds)}
                      >
                        {formatTime(seconds)}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Or dial it in (H : M : S)</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={customHours}
                        onChange={(e) => setCustomHours(Math.max(0, Math.min(23, parseInt(e.target.value) || 0)))}
                        className="h-10 w-16 rounded-md border border-input bg-background px-2 py-2 text-center text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        placeholder="H"
                      />
                      <span className="text-lg font-semibold">:</span>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={customMinutes}
                        onChange={(e) => setCustomMinutes(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                        className="h-10 w-16 rounded-md border border-input bg-background px-2 py-2 text-center text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        placeholder="M"
                      />
                      <span className="text-lg font-semibold">:</span>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={customSeconds}
                        onChange={(e) => setCustomSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                        className="h-10 w-16 rounded-md border border-input bg-background px-2 py-2 text-center text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        placeholder="S"
                      />
                    </div>
                    <Button onClick={applyCustomTime} disabled={customHours === 0 && customMinutes === 0 && customSeconds === 0}>
                      Apply
                    </Button>
                  </div>
                  {customTime > 0 && (
                    <div className="text-sm text-muted-foreground">
                      {currentCustomTime > 0 && currentCustomTime !== customTime ? (
                        <>
                          Preview: <span className="font-mono font-semibold">{formatTime(currentCustomTime)}</span>
                          {" • "}Selected: <span className="font-mono font-semibold">{formatTime(customTime)}</span>
                        </>
                      ) : (
                        <>Selected: <span className="font-mono font-semibold">{formatTime(customTime)}</span></>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Background Music */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Music4 className="h-4 w-4 text-primary" />
                Background Music
              </Label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {backgroundSounds.map((sound) => {
                  const isSelected = (selectedGame && selectedGame !== "Other" && gameSettings[selectedGame]?.music?.value === sound.value) ||
                                    (customMusic?.value === sound.value);
                  return (
                    <div
                      key={sound.value}
                      className={`flex cursor-pointer items-center justify-between rounded-lg border-2 p-4 transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/5 shadow-md'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                      onClick={() => setCustomMusic(sound)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`h-3 w-3 rounded-full border-2 ${
                          isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'
                        }`} />
                        <div>
                          <div className="font-medium">{sound.label}</div>
                          <div className="text-xs text-muted-foreground">
                            Duration: {formatTime(sound.duration)}
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          testSound(sound, musicVolume);
                        }}
                      >
                        Test
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Advanced settings: announcements, ending sound, volumes. */}
        <Card className="mt-6 border-border bg-card/70 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-xl">Bells &amp; Whistles</CardTitle>
            <CardDescription>Time announcements, an ending sound, and volume levels.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="enable-warnings"
                  checked={enableWarnings}
                  onCheckedChange={setEnableWarnings}
                />
                <Label htmlFor="enable-warnings" className="cursor-pointer">
                  Enable time announcements
                </Label>
              </div>

              {enableWarnings && (
                <div className="space-y-3 rounded-lg border border-border p-4">
                  <p className="text-xs text-muted-foreground">
                    A friendly voice calls out the time as it runs down (e.g. "5 seconds left").
                  </p>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {timeAnnouncementOptions
                      .filter(announcement => announcement.time < maxTime || maxTime === 0)
                      .map((announcement) => {
                        const isEnabled = enabledAnnouncements.some(a => a.time === announcement.time);
                        return (
                          <div
                            key={announcement.time}
                            className={`flex items-center justify-between rounded-lg border p-3 transition-colors cursor-pointer ${
                              isEnabled ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                            }`}
                            onClick={() => toggleAnnouncement(announcement)}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={isEnabled}
                                onCheckedChange={() => toggleAnnouncement(announcement)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div className="text-sm font-medium">{announcement.label}</div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                testSound(announcement, warningVolume);
                              }}
                              className="shrink-0"
                            >
                              Test
                            </Button>
                          </div>
                        );
                      })}
                  </div>

                  {enabledAnnouncements.length === 0 && (
                    <p className="py-1 text-center text-sm text-muted-foreground">
                      None selected yet — pick a few above.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-2">
                <Label htmlFor="ending-sound">Ending Sound</Label>
                <Select
                  value={endingSound?.value || undefined}
                  onValueChange={(value) => setEndingSound(endingSounds.find(s => s.value === value))}
                >
                  <SelectTrigger id="ending-sound">
                    <SelectValue placeholder="Choose Sound" />
                  </SelectTrigger>
                  <SelectContent>
                    {endingSounds.map(sound => (
                      <SelectItem key={sound.value} value={sound.value}>
                        {sound.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {endingSound && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => testSound(endingSound, endingVolume)}
                >
                  Test
                </Button>
              )}
            </div>

            <div className="space-y-4 rounded-lg border border-border p-4">
              {[
                { id: 'music-volume', label: 'Background music', value: musicVolume, set: setMusicVolume },
                { id: 'warning-volume', label: 'Announcements', value: warningVolume, set: setWarningVolume },
                { id: 'ending-volume', label: 'Ending sound', value: endingVolume, set: setEndingVolume },
              ].map(({ id, label, value, set }) => (
                <div key={id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor={id}>{label} volume</Label>
                    <span className="text-sm text-muted-foreground">{Math.round(value * 100)}%</span>
                  </div>
                  <input
                    id={id}
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={value}
                    onChange={(e) => set(parseFloat(e.target.value))}
                    className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-secondary accent-primary"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Saved presets. */}
        <Card className="mt-6 border-border bg-card/70 backdrop-blur">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Star className="h-5 w-5 text-primary" />
              Saved Presets
            </CardTitle>
            <CardDescription>Bottle up a setup for next game night.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                placeholder="Preset name..."
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && savePreset()}
                className="h-10 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
              <Button onClick={savePreset} disabled={!presetName.trim()} className="sm:min-w-[140px]">
                Save Preset
              </Button>
            </div>

            {savedPresets.length > 0 ? (
              <div className="space-y-2">
                {savedPresets.map((preset) => (
                  <div
                    key={preset.id}
                    className="flex flex-col items-start justify-between gap-2 rounded-lg border border-border p-3 transition-colors hover:bg-muted/50 sm:flex-row sm:items-center"
                  >
                    <span className="font-medium">{preset.name}</span>
                    <div className="flex w-full gap-2 sm:w-auto">
                      <Button variant="outline" size="sm" onClick={() => loadPreset(preset)} className="flex-1 sm:flex-none">
                        Load
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => deletePreset(preset.id)} className="flex-1 sm:flex-none">
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No saved presets yet. Create one above!
              </p>
            )}
          </CardContent>
        </Card>

        {/* Credits — quietly tucked at the bottom. */}
        <p className="mt-8 text-center text-xs text-muted-foreground/80">
          Music: "Sergio's Magic Dustbin" by Kevin MacLeod (incompetech.com) —{" "}
          <a
            href="http://creativecommons.org/licenses/by/4.0/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline-offset-2 hover:text-primary hover:underline"
          >
            CC BY 4.0
          </a>
        </p>
      </div>
    </div>
  );
};

export default Timer;
